// A tile's interior as a screen of the game (PRD section 19.6): the 3D interior on its nearly top-down
// camera, the blueprint-mode build session, the HUD, the floating figures and the machine card, all
// fed by the running H1 simulation. The app routes gestures and frames here while the interior is open.
import type { Vector3 } from 'three';
import type { Game } from '../game';
import { beltTracks, itemPoses, type BeltTrack } from '../interior/belt-tracks';
import { BuildSession, type Tool } from '../interior/build-session';
import { buildGhost } from '../interior/ghost';
import type { ScreenPoint, OneFinger } from '../input/gestures';
import { CameraRig, type CameraMode, type Rect } from '../render/camera-rig';
import { InteriorScene, type CellPoint } from '../render/interior-scene';
import type { Cell } from '../sim/geometry';
import type { MachineReport, PortReport, Report, TileReport } from '../sim/report';
import { InteriorHud, type BuildChoice, type BuildItem, type InteriorHudState, type Production } from '../ui/interior-hud';
import { InteriorLabels } from '../ui/interior-labels';
import { MachineCard } from '../ui/machine-card';

/** The item passing a door: what flows through it between the store and the interior, else what waits in it. */
function doorItem(port: PortReport): string | undefined {
  const flowing = port.kind === 'in' ? port.leaving : port.arriving;
  return (flowing[0] ?? port.stock[0])?.item;
}

export interface InteriorViewHandlers {
  /** «Isla», or pinching out past the widest view. */
  readonly onBack: () => void;
  /** Something on screen changed: draw a frame. */
  readonly invalidate: () => void;
  /** The layout changed: the shadow map must be redrawn. */
  readonly invalidateShadows: () => void;
}

/** A pinch that keeps going this far past the widest view goes back to the island. */
const EXIT_PINCH = 0.72;

export class InteriorView {
  readonly scene: InteriorScene;
  readonly rig: CameraRig;
  readonly session: BuildSession;
  readonly hud: InteriorHud;
  readonly labels: InteriorLabels;
  readonly card: MachineCard;
  private readonly game: Game;
  private readonly tileId: string;
  private readonly handlers: InteriorViewHandlers;
  private reportRef: Report | undefined;
  private tracks: BeltTrack[] = [];
  private moving = false;
  private width = 1;
  private height = 1;
  /** How much of a pinch the zoom limit held back during this gesture. */
  private overflow = 1;
  /** A short message replacing the tool hint for a moment (why something could not be done). */
  private flash: { text: string; until: number } | undefined;
  private selected: string | undefined;
  /** What the card was last placed for: it only moves when the camera or the machine does. */
  private cardKey = '';

  constructor(game: Game, tileId: string, opts: { readonly shadows: boolean; readonly camera: CameraMode }, handlers: InteriorViewHandlers) {
    this.game = game;
    this.tileId = tileId;
    this.handlers = handlers;
    const tile = this.tile(game.report());
    this.scene = new InteriorScene(tile, game.data, { shadows: opts.shadows });
    this.rig = new CameraRig(this.scene.framing);
    this.rig.setMode(opts.camera);
    this.session = new BuildSession(game.sim, tileId);
    const items: BuildItem[] = [
      { id: 'belt', label: game.data.belt.name, choice: { kind: 'belt' } },
      ...[...game.data.machines.values()].map((m) => ({ id: m.id, label: m.name, choice: { kind: 'machine', machine: m.id } as const, product: [...m.recipe.out.keys()][0] ?? '' })),
      { id: 'remove', label: 'Quitar', choice: { kind: 'remove' } },
    ];
    this.hud = new InteriorHud(items, {
      onBack: handlers.onBack,
      onChoose: (choice) => {
        this.choose(choice);
      },
      onConfirm: () => {
        this.say(this.session.confirm());
        this.changed();
      },
      onRotate: () => {
        this.session.rotate();
        this.changed();
      },
      onCancel: () => {
        this.cancel();
      },
      onUndo: () => {
        const label = game.sim.undo();
        if (!label) this.flashHint('No hay nada que deshacer.');
        this.changed();
      },
      onRedo: () => {
        const label = game.sim.redo();
        if (!label) this.flashHint('No hay nada que rehacer.');
        this.changed();
      },
    });
    this.hud.setTitle(`${tile.name} · ${tile.terrain} ${tile.interior?.size.join('×') ?? ''}`);
    this.labels = new InteriorLabels();
    this.card = new MachineCard({
      onRotate: () => {
        const m = this.selectedMachine();
        if (m) this.say(game.sim.execute({ type: 'rotate', tile: tileId, cell: m.origin }));
        this.changed();
      },
      onMove: () => {
        const m = this.selectedMachine();
        this.select(undefined);
        if (m) this.session.startMove(m.origin);
        this.changed();
      },
      onRemove: () => {
        const m = this.selectedMachine();
        this.select(undefined);
        if (m) this.say(this.session.remove(m.origin));
        this.changed();
      },
      onClose: () => {
        this.select(undefined);
        this.changed();
      },
    });
    this.sync(true);
  }

  /** The HUD, labels and card, to add over the scene. */
  get overlays(): HTMLElement[] {
    return [this.labels.el, this.hud.el, this.card.el];
  }

  setVisible(visible: boolean): void {
    this.hud.el.hidden = !visible;
    this.labels.el.hidden = !visible;
    if (!visible) {
      this.select(undefined);
      this.card.hide();
    }
  }

  setMode(mode: CameraMode, now: number): void {
    this.rig.setMode(mode, now);
  }

  /** Lays the camera out for the screen, keeping the block clear of the HUD's panels (the interior's and `shared`). */
  resize(width: number, height: number, origin: DOMRect, shared: readonly Rect[]): void {
    this.width = width;
    this.height = height;
    this.rig.setViewport({ width, height, obstacles: [...this.hud.obstacles(origin), ...shared] });
  }

  /** True while the items ride the belts and need frames (ambient animation). */
  get animating(): boolean {
    return this.moving;
  }

  // ------------------------------------------------------------ input ---

  /** One finger draws while placing belts or machines; otherwise it pans and taps. */
  oneFinger = (_p: ScreenPoint): OneFinger => {
    const kind = this.session.tool?.kind;
    return kind === 'belt' || kind === 'machine' || kind === 'move' ? 'draw' : 'pan';
  };

  /** «Cancelar» (or Escape): drops the ghost; with no ghost, puts the tool down. */
  cancel(): void {
    this.session.cancel();
    this.changed();
  }

  /** Puts the tool down and drops its ghost, as when leaving for the island. */
  putDown(): void {
    this.choose(undefined);
  }

  drawStart(p: ScreenPoint): void {
    const point = this.cellPoint(p);
    if (!point) return;
    const tool = this.session.tool;
    if (tool?.kind === 'belt') {
      const cell = this.scene.cellAt(point);
      if (cell) this.session.beginBelt(cell);
    } else {
      this.session.beginMachine(point);
    }
    this.changed();
  }

  draw(p: ScreenPoint): void {
    const point = this.cellPoint(p);
    if (!point) return;
    if (this.session.tool?.kind === 'belt') {
      const cell = this.scene.cellAt(point);
      if (cell) this.session.extendBelt(cell);
    } else {
      this.session.placeMachineAt(point);
    }
    this.changed();
  }

  drawEnd(): void {
    this.session.endDrag();
  }

  drawCancel(): void {
    this.session.cancelDrag();
    this.changed();
  }

  tap(p: ScreenPoint): void {
    const point = this.cellPoint(p);
    const cell = point && this.scene.cellAt(point);
    if (this.session.tool?.kind === 'remove') {
      if (cell) this.say(this.session.remove(cell));
      this.changed();
      return;
    }
    const machine = cell ? this.session.machineAt(cell) : undefined;
    this.select(machine && machine.id !== this.selected ? machine.id : undefined);
    this.changed();
  }

  beginGesture(): void {
    this.overflow = 1;
    this.rig.beginGesture();
  }

  pan(from: ScreenPoint, to: ScreenPoint, now: number): void {
    this.rig.pan(from, to, this.width, this.height, now);
  }

  zoom(factor: number, at: ScreenPoint): void {
    this.overflow *= this.rig.zoom(factor, at, this.width, this.height);
  }

  /** Ends a camera gesture; a pinch that kept going past the widest view leaves for the island. */
  endGesture(now: number): void {
    this.rig.endGesture(now);
    if (this.overflow < EXIT_PINCH) this.handlers.onBack();
    this.overflow = 1;
  }

  /** The screen point of a point in cell units, for tests. */
  screenOf(x: number, y: number, height = 0): ScreenPoint {
    const p = this.scene.toWorld(x, y, height);
    return this.rig.project(p.x, p.y, p.z, this.width, this.height);
  }

  /** Screen box around cells, from the ground up to `height`. */
  private screenBox(cells: readonly Cell[], height: number): Rect {
    const xs = cells.map((c) => c[0]);
    const ys = cells.map((c) => c[1]);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs) + 1;
    const y0 = Math.min(...ys);
    const y1 = Math.max(...ys) + 1;
    const pts = [x0, x1].flatMap((x) => [y0, y1].flatMap((y) => [this.screenOf(x, y), this.screenOf(x, y, height)]));
    return { left: Math.min(...pts.map((p) => p.x)), top: Math.min(...pts.map((p) => p.y)), right: Math.max(...pts.map((p) => p.x)), bottom: Math.max(...pts.map((p) => p.y)) };
  }

  /** Smallest side of the middle cell on screen, in CSS pixels: the touch target a cell offers. */
  cellPixels(): number {
    const [w, h] = this.scene.size;
    const c = Math.floor(w / 2);
    const r = Math.floor(h / 2);
    const a = this.screenOf(c, r);
    const b = this.screenOf(c + 1, r);
    const d = this.screenOf(c, r + 1);
    const e1 = { x: b.x - a.x, y: b.y - a.y };
    const e2 = { x: d.x - a.x, y: d.y - a.y };
    // The narrowest width of the cell's parallelogram: its area over its longest side.
    const area = Math.abs(e1.x * e2.y - e1.y * e2.x);
    return area / Math.max(Math.hypot(e1.x, e1.y), Math.hypot(e2.x, e2.y));
  }

  // ------------------------------------------------------------ frames ---

  /** Called on every drawn frame: refreshes from the simulation and moves the items. */
  frame(now: number): void {
    this.sync(false);
    this.scene.setItems(this.moving || this.tracks.length > 0 ? itemPoses(this.tracks, this.game.visualMinutes) : []);
    const ghost = this.session.ghost();
    this.scene.setGhost(ghost && buildGhost(ghost), this.moving ? Math.sin(now / 220) : 0);
    if (this.flash && now > this.flash.until) {
      this.flash = undefined;
      this.renderHud();
    }
    this.placeOverlays();
  }

  /** Puts the labels, the marker and the card where the camera shows their anchors. */
  private placeOverlays(): void {
    this.labels.update(this.rig.camera, this.width, this.height);
    const m = this.selectedMachine();
    const cardKey = `${m?.id ?? ''}:${m?.origin.join(',') ?? ''}:${m?.rotation ?? ''}:${this.width}x${this.height}:${this.rig.camera.matrixWorld.elements.join(',')}`;
    if (cardKey === this.cardKey) return;
    this.cardKey = cardKey;
    if (m && !this.card.el.hidden) this.card.place(this.screenBox(m.cells, 1.2), this.width, this.height, { panels: this.rig.viewport.obstacles, figures: this.labels.doorRects(), tile: this.screenBox([[0, 0], [this.scene.size[0] - 1, this.scene.size[1] - 1]], 0) });
  }

  dispose(): void {
    for (const el of this.overlays) el.remove();
    this.scene.dispose();
  }

  // ---------------------------------------------------------- internals ---

  private tile(report: Report): TileReport {
    const tile = report.tiles.find((t) => t.id === this.tileId);
    if (!tile?.interior) throw new Error(`La casilla «${this.tileId}» no tiene interior`);
    return tile;
  }

  private cellPoint(p: ScreenPoint): CellPoint | undefined {
    const ground = this.rig.pick(p, this.width, this.height);
    return ground ? this.scene.toCell(ground) : undefined;
  }

  private choose(choice: BuildChoice | undefined): void {
    this.select(undefined);
    const tool: Tool | undefined = choice?.kind === 'machine' ? { kind: 'machine', machine: choice.machine } : choice;
    this.session.selectTool(tool);
    this.changed();
  }

  private select(id: string | undefined): void {
    this.selected = id;
    if (!id) this.card.hide();
    this.labels.setMarker(id ? this.scene.machineAnchors.get(id) : undefined);
  }

  private selectedMachine(): MachineReport | undefined {
    return this.tile(this.game.report()).interior?.machines.find((m) => m.id === this.selected);
  }

  private say(outcome: { ok: true } | { ok: false; reason: string }): void {
    if (!outcome.ok) this.flashHint(outcome.reason);
  }

  private flashHint(text: string): void {
    this.flash = { text, until: performance.now() + 2600 };
  }

  /** A command ran or the ghost moved: refresh now and draw. */
  private changed(): void {
    this.sync(true);
    // The card and the marker that a touch just opened are placed now, not on the next drawn frame:
    // a slow frame would show them for a moment in the corner, where a second touch could miss them.
    this.placeOverlays();
    this.handlers.invalidate();
  }

  private sync(force: boolean): void {
    const report = this.game.report();
    if (!force && report === this.reportRef) return;
    this.reportRef = report;
    const tile = this.tile(report);
    const interior = tile.interior;
    if (!interior) return;
    if (this.scene.setLayout(interior)) this.handlers.invalidateShadows();
    this.tracks = beltTracks(interior.belts);
    // Items glide only while the clock runs: paused or frozen for a screenshot, they stand still.
    this.moving = !report.paused && !this.game.frozen && interior.belts.some((b) => b.rate > 1e-9);

    for (const port of tile.ports) {
      const anchor = this.scene.doorAnchors.get(port.id);
      if (!anchor) continue;
      const rate = port.kind === 'in' ? port.outRate : port.inRate;
      this.labels.setDoor(port.id, anchor, port.kind, rate, doorItem(port));
    }
    this.labels.setMachines(
      interior.machines.flatMap((m) => {
        const anchor = this.scene.machineAnchors.get(m.id);
        return anchor ? [{ id: m.id, anchor, status: m.status, work: m.work }] : [];
      }),
    );
    if (this.selected && !interior.machines.some((m) => m.id === this.selected)) this.select(undefined);
    if (this.selected) this.labels.setMarker(this.scene.machineAnchors.get(this.selected));
    const m = this.selectedMachine();
    const def = m && this.game.data.machines.get(m.type);
    if (m && def) this.card.show(m, def, (id) => this.game.data.items.get(id)?.name ?? id);
    this.hud.setProduction(this.production(tile));
    this.renderHud();
  }

  private production(tile: TileReport): Production {
    const exit = tile.ports.find((p) => p.kind === 'out');
    const machines = tile.interior?.machines ?? [];
    const work = machines.length ? machines.reduce((s, m) => s + m.work, 0) / machines.length : 0;
    const itemId = exit && doorItem(exit);
    const item = itemId ? { id: itemId, name: this.game.data.items.get(itemId)?.name ?? itemId } : undefined;
    return { rate: exit?.inRate ?? 0, item, work };
  }

  private renderHud(): void {
    this.hud.render(this.hudState());
  }

  private hudState(): InteriorHudState {
    const tool = this.session.tool;
    const ghost = this.session.ghost();
    const report = this.reportRef ?? this.game.report();
    const chosen = tool?.kind === 'machine' ? tool.machine : tool?.kind === 'move' ? undefined : tool?.kind;
    const placing = tool?.kind === 'belt' || tool?.kind === 'machine' || tool?.kind === 'move';
    let hint: InteriorHudState['hint'];
    if (this.flash) hint = { text: this.flash.text, tone: 'amber' };
    else if (tool?.kind === 'remove') hint = { text: 'Toca una máquina o una cinta para quitarla.', tone: 'info' };
    else if (ghost && !ghost.fits) hint = { text: ghost.reason ?? 'No cabe aquí.', tone: 'amber' };
    else if (tool?.kind === 'belt') hint = ghost ? { text: 'La cinta cabe: confírmala, gírala o vuelve a trazarla.', tone: 'ok' } : { text: 'Arrastra el dedo sobre la cuadrícula para trazar la cinta.', tone: 'info' };
    else if (tool?.kind === 'machine') hint = ghost ? { text: 'Cabe aquí: arrástrala, gírala o confírmala.', tone: 'ok' } : { text: 'Toca la cuadrícula donde quieras poner la máquina.', tone: 'info' };
    else if (tool?.kind === 'move') hint = ghost?.changed ? { text: 'Cabe aquí: confirma para moverla.', tone: 'ok' } : { text: 'Arrastra la máquina a su nuevo sitio.', tone: 'info' };
    return {
      chosen,
      placing,
      removing: tool?.kind === 'remove',
      canConfirm: !!ghost && ghost.fits && ghost.changed,
      canRotate: !!ghost,
      hint,
      undo: report.undo,
      redo: report.redo,
    };
  }

  /** Anchor of a machine's figure, for tests. */
  machineAnchor(id: string): Vector3 | undefined {
    return this.scene.machineAnchors.get(id);
  }

  /** The cell under a screen point, for tests. */
  cellUnder(p: ScreenPoint): Cell | undefined {
    const point = this.cellPoint(p);
    return point && this.scene.cellAt(point);
  }
}
